import { useEffect, useState } from 'react'
import { K, mono, withheldLabel } from '../lib/theme'
import { fUsd, fBps, fAge, fDateET, short } from '../lib/format'
import { getSeries, type Board, type SeriesPoint } from '../lib/api'

const RANGES: [string, string][] = [['24h', '24 h'], ['7d', '7 d'], ['30d', '30 d']]

/** On-chain price against the reference. The reference is drawn as a stepped
 *  line that holds flat while the market is shut — one line moving while the
 *  other stands still is the entire product, so it is the hero here. */
function Chart({ points }: { points: SeriesPoint[] }) {
  const pts = points.filter((p) => p.onchain_usd != null)
  if (pts.length < 2) {
    return <div style={{ padding: '40px 16px', color: K.dim, fontSize: 13, fontFamily: mono }}>
      Not enough samples yet to draw a line. Collecting every 5 minutes.
    </div>
  }
  const W = 1000, H = 260, PADL = 56, PADB = 26, PADT = 12
  const xs = pts.map((p) => new Date(p.ts).getTime())
  const vals = pts.flatMap((p) => [p.onchain_usd!, p.ref_usd ?? p.onchain_usd!])
  const x0 = Math.min(...xs), x1 = Math.max(...xs)
  let lo = Math.min(...vals), hi = Math.max(...vals)
  const pad = (hi - lo) * 0.12 || Math.max(hi * 0.001, 0.01)
  lo -= pad; hi += pad
  const px = (t: number) => PADL + ((t - x0) / Math.max(1, x1 - x0)) * (W - PADL - 12)
  const py = (v: number) => PADT + (1 - (v - lo) / Math.max(1e-9, hi - lo)) * (H - PADT - PADB)

  const onchain = pts.map((p, i) => `${i ? 'L' : 'M'}${px(xs[i]).toFixed(1)},${py(p.onchain_usd!).toFixed(1)}`).join('')
  // Stepped: the reference only changes when a new print lands.
  let ref = ''
  let prevY: number | null = null
  pts.forEach((p, i) => {
    if (p.ref_usd == null) return
    const X = px(xs[i]), Y = py(p.ref_usd)
    if (prevY == null) ref += `M${X.toFixed(1)},${Y.toFixed(1)}`
    else ref += `L${X.toFixed(1)},${prevY.toFixed(1)}L${X.toFixed(1)},${Y.toFixed(1)}`
    prevY = Y
  })
  const closedBands: { x: number; w: number }[] = []
  let bandStart: number | null = null
  pts.forEach((p, i) => {
    const closed = p.session_state !== 'open'
    if (closed && bandStart == null) bandStart = px(xs[i])
    if (!closed && bandStart != null) { closedBands.push({ x: bandStart, w: px(xs[i]) - bandStart }); bandStart = null }
  })
  if (bandStart != null) closedBands.push({ x: bandStart, w: px(x1) - bandStart })

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="On-chain price against the reference print">
      {closedBands.map((b, i) => <rect key={i} x={b.x} y={PADT} width={Math.max(0, b.w)} height={H - PADT - PADB} fill="rgba(255,184,77,.06)" />)}
      {[0, 0.5, 1].map((f) => {
        const v = lo + (hi - lo) * (1 - f), y = py(v)
        return <g key={f}>
          <line x1={PADL} x2={W - 12} y1={y} y2={y} stroke="rgba(154,163,199,.14)" strokeWidth={1} />
          <text x={PADL - 8} y={y + 3} textAnchor="end" fill={K.faint} fontSize={10} fontFamily={mono}>{v.toFixed(2)}</text>
        </g>
      })}
      <path d={ref} fill="none" stroke={K.cy} strokeWidth={1.6} strokeDasharray="none" opacity={0.95} />
      <path d={onchain} fill="none" stroke={K.am} strokeWidth={1.8} />
      <text x={PADL} y={H - 8} fill={K.faint} fontSize={10} fontFamily={mono}>{fDateET(new Date(x0))} ET</text>
      <text x={W - 12} y={H - 8} textAnchor="end" fill={K.faint} fontSize={10} fontFamily={mono}>{fDateET(new Date(x1))} ET</text>
    </svg>
  )
}

export function Ticker({ sym, board, setSym, goHeat }: {
  sym: string; board: Board | null; setSym: (s: string) => void; goHeat: () => void
}) {
  const [range, setRange] = useState('24h')
  const [points, setPoints] = useState<SeriesPoint[]>([])
  const [note, setNote] = useState<string | undefined>()
  const s = board?.tokens.find((t) => t.sym === sym)

  useEffect(() => {
    let live = true
    getSeries(sym, range).then((r) => { if (live) { setPoints(r.points); setNote(r.note) } }).catch(() => live && setPoints([]))
    return () => { live = false }
  }, [sym, range])

  const printed = points.filter((p) => p.premium_bps != null)
  const open = printed.filter((p) => p.session_state === 'open').map((p) => p.premium_bps!)
  const closed = printed.filter((p) => p.session_state !== 'open').map((p) => p.premium_bps!)
  const mean = (a: number[]) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null)

  return (
    <main style={{ padding: '20px clamp(16px,3vw,28px) 0', maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8, marginBottom: 16 }}>
        {(board?.tokens ?? []).map((t) => (
          <button key={t.sym} onClick={() => setSym(t.sym)} style={{
            fontFamily: mono, fontSize: 12.5, padding: '6px 10px',
            border: `1px solid ${t.sym === sym ? 'rgba(238,242,255,.6)' : 'rgba(154,163,199,.25)'}`,
            background: t.sym === sym ? 'rgba(238,242,255,.1)' : 'transparent',
            color: t.sym === sym ? K.txt : K.mut, borderRadius: 2, whiteSpace: 'nowrap', flex: 'none', cursor: 'pointer',
          }}>{t.sym}</button>
        ))}
      </div>

      <section style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 40px', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: mono, fontSize: 30, fontWeight: 500 }}>{sym}</span>
            <span style={{ fontSize: 14, color: K.mut }}>tokenized · tracks {s?.name ?? '—'}</span>
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: K.dim, maxWidth: '60ch', lineHeight: 1.45 }}>
            An on-chain token on Robinhood Chain priced in its Uniswap pool. Holding it is not ownership of the share.
          </p>
          {s && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginTop: 10, fontFamily: mono, fontSize: 12, color: K.mut }}>
              <span title={s.addr}><span style={{ color: K.dim, fontSize: 10.5, letterSpacing: '.06em' }}>TOKEN </span>{short(s.addr, 8, 6)}</span>
              <span title={s.pool}><span style={{ color: K.dim, fontSize: 10.5, letterSpacing: '.06em' }}>POOL </span>{short(s.pool, 8, 6)}</span>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {RANGES.map(([v, label]) => (
            <button key={v} onClick={() => setRange(v)} style={{
              fontFamily: mono, fontSize: 12, padding: '6px 12px',
              border: `1px solid ${range === v ? K.txt : 'rgba(238,242,255,.35)'}`,
              background: range === v ? K.txt : 'transparent', color: range === v ? K.bg : K.txt, borderRadius: 2, cursor: 'pointer',
            }}>{label}</button>
          ))}
        </div>
      </section>

      <section className="ob-stats" style={{ display: 'grid', gap: 12, marginBottom: 18 }}>
        {[
          { label: 'Now', value: s?.withheld ? withheldLabel(s.withheld) : fBps(s?.premiumBps), sub: s?.refAt ? `reference ${fAge(s.refAgeMs)} old` : 'no reference' },
          { label: 'Session · reference live', value: fBps(mean(open)), sub: `${open.length} printed samples, 09:30–16:00 ET` },
          { label: 'Market closed · reference stale', value: fBps(mean(closed)), sub: `${closed.length} printed samples` },
          { label: 'On-chain now', value: fUsd(s?.onchainUsd), sub: s?.refUsd ? `reference ${fUsd(s.refUsd)}` : 'no public reference' },
        ].map((c) => (
          <div key={c.label} style={{ border: `1px solid ${K.line}`, padding: '12px 14px' }}>
            <div style={{ fontFamily: mono, fontSize: 10.5, color: K.dim, letterSpacing: '.06em', textTransform: 'uppercase' }}>{c.label}</div>
            <div style={{ fontFamily: mono, fontSize: 22, marginTop: 6, color: K.txt }}>{c.value}</div>
            <div style={{ fontSize: 11.5, color: K.dim, marginTop: 3 }}>{c.sub}</div>
          </div>
        ))}
      </section>

      <section style={{ border: `1px solid ${K.line}`, padding: '14px 16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontFamily: mono, fontSize: 11, color: K.dim, marginBottom: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 14, height: 2, background: K.am }} />on-chain</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 14, height: 2, background: K.cy }} />reference (last print)</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 10, height: 10, background: 'rgba(255,184,77,.14)' }} />market closed</span>
        </div>
        <Chart points={points} />
        {note && <div style={{ marginTop: 8, fontSize: 12, color: K.dim, fontFamily: mono }}>{note}</div>}
      </section>

      <p style={{ fontSize: 12, color: K.dim, lineHeight: 1.5, margin: '14px 0 0', maxWidth: '80ch' }}>
        Means are over printed samples only; session and closed-market samples are never pooled. A premium computed against a
        stale reference is a different claim from one computed mid-session, and is reported separately.{' '}
        <button onClick={goHeat} style={{ background: 'none', border: 0, padding: 0, color: K.cy, fontSize: 'inherit', fontFamily: 'inherit', cursor: 'pointer' }}>
          Hour-of-week heatmap for {sym} ↗
        </button>
      </p>
    </main>
  )
}
